import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { DocumentViewer, GeneralNotFound, Spinner } from '@repo/components';
import { PERMISSIONS } from '@repo/shared/domain';
import { getVerifyPermission } from '@repo/shared/helpers';
import { useAuthenticatedUser } from 'src/hooks/useAuthenticatedUser';
import { getEventDocument } from 'src/api/admin/events';
import { getDocumentTypes } from 'src/api/admin/documentTypes';

export const Route = createFileRoute('/_admin/events/$eventId/documents/$documentId')({
	component: DocumentDetailPage,
});

function DocumentDetailPage() {
	const { eventId, documentId } = Route.useParams();
	const navigate = useNavigate();
	const queryClient = useQueryClient();

	const { user: authenticatedUser, loading: authLoading } = useAuthenticatedUser();

	const { data: document, isLoading: documentLoading } = useQuery({
		queryKey: ['event-document', eventId, documentId],
		queryFn: () => getEventDocument(eventId, documentId),
	});

	const { data: documentTypesData, isLoading: typesLoading } = useQuery({
		queryKey: ['document-types', 'events'],
		queryFn: () => getDocumentTypes('events'),
	});

	const verifyPermission = authenticatedUser
		? getVerifyPermission(authenticatedUser.permissions)
		: () => false;

	const handleClose = () => {
		navigate({ to: '/events/$eventId/documents', params: { eventId } });
	};

	const handleUpdate = () => {
		queryClient.invalidateQueries({ queryKey: ['event-document', eventId, documentId] });
		queryClient.invalidateQueries({ queryKey: ['event-documents', eventId] });
	};

	const isLoading = authLoading || documentLoading || typesLoading;
	if (isLoading) {
		return (
			<div className="h-full w-full flex items-center justify-center p-8">
				<Spinner />
			</div>
		);
	}

	if (!document || !authenticatedUser) {
		return (
			<GeneralNotFound
				label="We couldn't find this document. Maybe it has been deleted?"
				action={{
					label: 'Return to documents',
					href: `/events/${eventId}/documents`,
					variant: 'outlined',
				}}
			/>
		);
	}

	return (
		<DocumentViewer
			document={document}
			onClose={handleClose}
			types={documentTypesData?.data || []}
			apiUrlBase={`/api/events/${eventId}/files`}
			onRevalidateDocument={handleUpdate}
			permissions={{
				metadata: verifyPermission([PERMISSIONS.adminEventsView]),
				fullView: verifyPermission([PERMISSIONS.adminEventsView]),
				sensitiveView: false,
				edit: verifyPermission([PERMISSIONS.adminEventsEdit]),
				archive: verifyPermission([PERMISSIONS.adminEventsEdit]),
				review: verifyPermission([PERMISSIONS.adminEventsEdit]),
			}}
		/>
	);
}
